import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ColorService {

  public constructor() {
  }

  public hexToRbg(hexColor: string): { red: number, green: number, blue: number } {
    if (!hexColor || hexColor.length < 4) {
      console.error('Invalid hex color string: ' + hexColor);
      return { red: 0, green: 0, blue: 0 };
    } else if (hexColor.length == 4) {
      // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      hexColor = hexColor.replace(shorthandRegex, function (m, r, g, b) {
        return r + r + g + g + b + b;
      });
    } else if (hexColor.length < 7) {
      console.error('Invalid hex color string: ' + hexColor);
      return { red: 0, green: 0, blue: 0 };
    }

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
    if (!result) {
      console.error('Invalid hex color string: ' + hexColor);
      return { red: 0, green: 0, blue: 0 };
    }
    return { red: parseInt(result[1], 16), green: parseInt(result[2], 16), blue: parseInt(result[3], 16) };
  }

  public rgbToHsl(rgb: { red: number, green: number, blue: number }): {
    hue: number,
    saturation: number,
    lightness: number
  } {

    rgb.red /= 255, rgb.green /= 255, rgb.blue /= 255;
    const max = Math.max(rgb.red, rgb.green, rgb.blue), min = Math.min(rgb.red, rgb.green, rgb.blue);
    let h, s, l = (max + min) / 2;
    const result = { hue: h ? +h : 0, saturation: s ? +s : 0, lightness: l ? +l : 0 };

    if (max == min) {
      result.hue = result.saturation = 0; // achromatic
    } else {
      const d = max - min;
      result.saturation = result.lightness > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case rgb.red:
          result.hue = (rgb.green - rgb.blue) / d + (rgb.green < rgb.blue ? 6 : 0);
          break;
        case rgb.green:
          result.hue = (rgb.blue - rgb.red) / d + 2;
          break;
        case rgb.blue:
          result.hue = (rgb.red - rgb.green) / d + 4;
          break;
      }
      result.hue /= 6;
    }
    return result;
  }

  /**
   * BT. 709 luma calculation
   * See: https://en.wikipedia.org/wiki/Luma_(video)
   */
  public getLuma(rgb: { red: number, green: number, blue: number }): number {
    return (0.2126 * rgb.red) + (0.7152 * rgb.green) + (0.0722 * rgb.blue);
  }

  private hueToRgb(p: number, q: number, t: number): number {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  /**
   * converts a HSL color to RGB
   * @param {{hue: number, saturation: number, lightness: number}} hsl
   * @returns {{red: number, green: number, blue: number}}
   */
  public hslToRgb(hsl: { hue: number, saturation: number, lightness: number }): {
    red: number,
    green: number,
    blue: number
  } {
    const result: { red: number, green: number, blue: number } = { red: 0, green: 0, blue: 0 };

    if (hsl.saturation == 0) {
      result.red = result.green = result.blue = hsl.lightness; // achromatic
    } else {

      const q = hsl.lightness < 0.5 ? hsl.lightness * (1 + hsl.saturation) : hsl.lightness + hsl.saturation - hsl.lightness * hsl.saturation;
      const p = 2 * hsl.lightness - q;
      result.red = this.hueToRgb(p, q, hsl.hue + 1 / 3);
      result.green = this.hueToRgb(p, q, hsl.hue);
      result.blue = this.hueToRgb(p, q, hsl.hue - 1 / 3);
    }
    result.red = Math.round(result.red * 255);
    result.green = Math.round(result.green * 255);
    result.blue = Math.round(result.blue * 255);
    return result;
  }

  /**
   * generates a contrasting color to the given hex color
   * @param {{red: number, green: number, blue: number}} rgb object
   * @returns {{red: number, green: number, blue: number}} rgb object
   */
  public generateContrastingColor(rgb: { red: number, green: number, blue: number }): {
    red: number,
    green: number,
    blue: number
  } {
    const luma = this.getLuma(rgb);
    const hslBackgroundColor = this.rgbToHsl(structuredClone(rgb));
    const hslForegroundColor = [hslBackgroundColor.hue, hslBackgroundColor.saturation, hslBackgroundColor.lightness];

    if (luma >= 165) { //Light bg
      hslForegroundColor[2] = .2;
    } else { //Dark bg
      hslForegroundColor[2] = 0.97;  //white text
    }

    return this.hslToRgb({
      hue: hslForegroundColor[0],
      saturation: hslForegroundColor[1],
      lightness: hslForegroundColor[2]
    });
  }


  /**
   * generates a contrasting color to the given hex color (a 500 bg color)
   * 500, 800 are the values in our color palette.
   * Figma palette: https://www.figma.com/design/sbV5EURS49KWQVPMY52cR8/Styleguide?node-id=2329-828&t=AR3uvRTQgD7nAYPA-4
   * @param hexBgColor: the hex background color value (should be in the 500 range(saturation 0.83, lightness 0.45-0.5))
   * @returns: a contrasting color to the given hex color
   */
  public generate800ContrastColor(hexBgColor: string | null): string {
    if (!hexBgColor || hexBgColor.length < 7) {
      console.error('Invalid hex color string: ' + hexBgColor);
      return '';
    }

    const hslColor = this.rgbToHsl(this.hexToRbg(hexBgColor));
    //Fix for white/black/gray colors
    //Make foreground color gray too if the background color is gray or very dark
    let saturation = hslColor.saturation < 0.25 ? hslColor.saturation : 0.96;
    if (hslColor.lightness < 0.14)
      saturation = Math.min(hslColor.saturation, 0.1);

    //Normally saturation and lightness are not fixed values but vary a bit depending on the palette and hue however
    //for the custom colors these should be somewhat accurate.
    return this.rgbToHex(this.hslToRgb({ hue: hslColor.hue, saturation: saturation, lightness: 0.32 }));
  }

  /**
   * generates a slightly more saturated color to use in charts based on the given hex color
   * @returns: a contrasting color to the given hex color
   */
  public generateChartColor(hexBgColor: string | null): string {
    if (!hexBgColor) {
      return '';
    }

    const hslColor = this.rgbToHsl(this.hexToRbg(hexBgColor));
    //In case we need to debug: console.debug(`%chexColor: ${hexBgColor}`, 'background-color:' + hexBgColor + ';color:#666');

    //Fix for white/black/gray colors
    //Make foreground color gray too if the background color is gray or very dark
    if (hslColor.saturation > 0.25 && hslColor.saturation < 0.85) {
      hslColor.saturation += 0.1;
    }

    if (hslColor.saturation > 0.25 && hslColor.lightness > 0.1 && hslColor.lightness < 0.96) { //adjust most colors to have average lightness
      hslColor.lightness = Math.min(0.8, Math.max(0.15, hslColor.lightness));
    } else if (hslColor.saturation < 0.25 && hslColor.saturation > 0.01) { //adjust almost white/black colors to be a bit darker/lighter
      hslColor.lightness = Math.min(0.95, Math.max(0.05, hslColor.lightness));
    }

    //Normally saturation and lightness are not fixed values but vary a bit depending on the palette and hue however
    //for the custom colors these should be somewhat accurate.
    const hexColor = this.rgbToHex(this.hslToRgb({
      hue: hslColor.hue,
      saturation: hslColor.saturation,
      lightness: hslColor.lightness
    }));
    return hexColor;
  }

  /**
   * converts a rgb object to a rgb string
   * @param {{ed: number, green: number, blue: number}} rgb
   * @returns {string} rgb(red, green, blue) string
   */
  public rbgToString(rgb: { red: number, green: number, blue: number }): string {
    return `rgb(${rgb.red}, ${rgb.green}, ${rgb.blue})`;
  }

  /**
   * Converts a rgb color to hex color string
   * @param {{red: number, green: number, blue: number}} rgb
   * @returns {string} hex color string
   */
  public rgbToHex(rgb: { red: number, green: number, blue: number }): string {
    return '#' + ((1 << 24) + (rgb.red << 16) + (rgb.green << 8) + rgb.blue).toString(16).slice(1).toUpperCase();
  }

  /**
   * creates a contrasting color to the given hex color OR white if the color is too dark
   * @param hexBgColor: the hex background color value
   * @returns: a contrasting color (white or black) to the given hex color
   */
  public generateTextRgbColor(hexBgColor: string): string {
    const textRgb = this.generateContrastingColor(this.hexToRbg(hexBgColor));
    return this.rbgToString(textRgb);
  }
}
